import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { Gift, CheckCircle, XCircle, ArrowRight, Info, X, Sparkles, Heart, RotateCw } from 'lucide-react'
import Terms from './Terms'
import Privacy from './Privacy'
import { getAuth, signInAnonymously } from 'firebase/auth'
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore'
import { db, app } from '../firebase/config'
import toast from 'react-hot-toast'

const PAYSTACK_PUBLIC_KEY = 'pk_live_d0dac42be7424384d1b9c6dc660d3993efaf5cd6'

function useQuery() {
  const { search } = useLocation()
  return new URLSearchParams(search)
}

const GiftCard = () => {
  const location = useLocation()
  const [giftCardCode, setGiftCardCode] = useState('')
  const [validationResult, setValidationResult] = useState(null)
  const [isValidating, setIsValidating] = useState(false)
  const [showWellnessForm, setShowWellnessForm] = useState(false)
  const [flippedCard, setFlippedCard] = useState(null) // 'session' or 'wellness' or null
  const [legalModalType, setLegalModalType] = useState(null) // 'terms' | 'privacy' | null
  
  // Wellness Gift Card Form State
  const [wellnessForm, setWellnessForm] = useState({
    amount: '',
    customAmount: '',
    creatorName: '',
    creatorPhone: '',
    recipientName: '',
    recipientPhone: '',
    message: '',
    deliveryMethod: 'digital', // 'digital' or 'physical'
    address: '' // For physical delivery
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paymentReference, setPaymentReference] = useState(null)
  const [showCompleteButton, setShowCompleteButton] = useState(false)

  // Toggle for applying a small uplift to cover payment charges
  const [applyPaystackFeeUplift, setApplyPaystackFeeUplift] = useState(true)
  const [promoDiscount, setPromoDiscount] = useState(0) // when > 0 overrides individual service discount
  const [allowedVisitTypes, setAllowedVisitTypes] = useState(['spa', 'mobile'])

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDocRef = doc(db, 'settings', 'booking')
        const snap = await getDoc(settingsDocRef)
        if (snap.exists()) {
          const data = snap.data()
          if (typeof data.applyPaystackFeeUplift === 'boolean') {
            setApplyPaystackFeeUplift(data.applyPaystackFeeUplift)
          }
          const promo = data.promoDiscount
          if (typeof promo === 'number' && promo >= 0) setPromoDiscount(promo)
          if (Array.isArray(data.allowedVisitTypes) && data.allowedVisitTypes.length > 0) {
            setAllowedVisitTypes(data.allowedVisitTypes)
          }
        } else {
          setAllowedVisitTypes(['spa', 'mobile'])
        }
      } catch (e) {
        console.error('Failed to load settings:', e)
      }
    }
    loadSettings()
  }, [])
  
  // Build deep-link for session gift card -> booking page
  const sessionBookingLink = (() => {
    if (!validationResult || !validationResult.valid) return null
    if (validationResult.type !== 'session' || !validationResult.cardDetails) return null
    const card = validationResult.cardDetails
    const params = new URLSearchParams()
    if (card.sessionCategory) params.set('category', card.sessionCategory)
    if (card.serviceType) params.set('type', card.serviceType)
    if (card.duration) params.set('duration', String(card.duration))
    if (card.price) params.set('price', String(card.price))
    if (card.visitType) params.set('visitType', String(card.visitType))
    if (card.people) params.set('people', String(card.people))
    params.set('fromSessionGiftCard', 'true')
    if (card.cardNumber) params.set('giftCardCode', card.cardNumber)
    return `/book?${params.toString()}`
  })()

  // Shared validation logic: can be called with a code string (e.g. from form or URL)
  const validateCode = async (codeStr) => {
    const raw = (codeStr || '').toString().trim()
    if (!raw) {
      toast.error('Please enter a gift card code')
      return
    }
    setIsValidating(true)
    setValidationResult(null)
    try {
      const cardNum = raw.replace(/\D/g, '').trim()
      if (cardNum.length < 8) {
        setValidationResult({
          valid: false,
          message: 'Invalid gift card code. Please check and try again.',
          balance: null,
          expiryDate: null,
          type: null,
          suspended: false,
          cardDetails: null
        })
        setIsValidating(false)
        return
      }

      // Query gift cards from Firestore
      const giftCardsRef = collection(db, 'giftCards')
      const snapshot = await getDocs(giftCardsRef)
      
      // Filter gift cards by cardNumber client-side
      const matchingCards = snapshot.docs.filter(doc => {
        const data = doc.data()
        return data.cardNumber === cardNum
      })

      if (matchingCards.length === 0) {
        setValidationResult({
          valid: false,
          message: 'Gift card not found. Please check the code and try again.',
          balance: null,
          expiryDate: null,
          type: null,
          suspended: false,
          cardDetails: null
        })
        setIsValidating(false)
        return
      }

      const giftCardDoc = matchingCards[0]
      const giftCard = { id: giftCardDoc.id, ...giftCardDoc.data() }

      // Check if suspended
      if (giftCard.suspended) {
        setValidationResult({
          valid: false,
          message: 'This gift card has been suspended. Please contact support for assistance.',
          balance: null,
          expiryDate: null,
          type: giftCard.type,
          suspended: true,
          cardDetails: giftCard
        })
        setIsValidating(false)
        return
      }

      // Check expiry date
      if (!giftCard.expiryDate) {
        setValidationResult({
          valid: false,
          message: 'This gift card is missing expiry information. Please contact support.',
          balance: null,
          expiryDate: null,
          type: giftCard.type,
          suspended: false,
          cardDetails: giftCard
        })
        setIsValidating(false)
        return
      }

      const expiry = new Date(giftCard.expiryDate)
      const now = new Date()
      
      if (expiry < now) {
        const expiryDateStr = expiry.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
        setValidationResult({
          valid: false,
          message: `This gift card expired on ${expiryDateStr}. Please contact support.`,
          balance: null,
          expiryDate: expiryDateStr,
          type: giftCard.type,
          suspended: false,
          cardDetails: giftCard
        })
        setIsValidating(false)
        return
      }

      // Check balance
      const balance = Number(giftCard.balance) || 0
      
      if (giftCard.type === 'session' && balance === 0) {
        setValidationResult({
          valid: false,
          message: 'This session gift card has already been used.',
          balance: 'GHS 0.00',
          expiryDate: expiry.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          type: giftCard.type,
          suspended: false,
          cardDetails: giftCard
        })
        setIsValidating(false)
        return
      }

      if (giftCard.type === 'wellness' && balance === 0) {
        setValidationResult({
          valid: false,
          message: 'This wellness gift card has no remaining balance.',
          balance: 'GHS 0.00',
          expiryDate: expiry.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          type: giftCard.type,
          suspended: false,
          cardDetails: giftCard
        })
        setIsValidating(false)
        return
      }

      // Valid gift card
      const expiryDateStr = expiry.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })

      setValidationResult({
        valid: true,
        message: 'Your gift card is valid!',
        balance: `GHS ${balance.toFixed(2)}`,
        expiryDate: expiryDateStr,
        type: giftCard.type,
        suspended: false,
        cardDetails: giftCard
      })
      
      toast.success('Gift card validated successfully!')
    } catch (error) {
      console.error('Error validating gift card:', error)
      setValidationResult({
        valid: false,
        message: 'Failed to validate gift card. Please try again later.',
        balance: null,
        expiryDate: null,
        type: null,
        suspended: false,
        cardDetails: null
      })
      toast.error('Failed to validate gift card')
    } finally {
      setIsValidating(false)
    }
  }

  const handleValidate = async (e) => {
    e.preventDefault()
    await validateCode(giftCardCode)
  }

  // When opened with ?code=XXX (e.g. from SMS link), prefill and auto-validate once
  const autoValidatedRef = useRef(false)
  useEffect(() => {
    if (autoValidatedRef.current) return
    const params = new URLSearchParams(location.search)
    const codeParam = params.get('code')
    if (!codeParam) return
    const cleaned = codeParam.replace(/\D/g, '').trim()
    if (cleaned.length < 8) return
    autoValidatedRef.current = true
    setGiftCardCode(cleaned)
    validateCode(cleaned)
    if (window.history.replaceState) {
      const u = new URL(window.location.href)
      u.searchParams.delete('code')
      window.history.replaceState({}, '', u.pathname + u.search)
    }
  }, [location.search])

  // Function to reset form (accessible throughout component)
  const resetForm = () => {
    setWellnessForm({
      amount: '',
      customAmount: '',
      creatorName: '',
      creatorPhone: '',
      recipientName: '',
      recipientPhone: '',
      message: '',
      deliveryMethod: 'digital',
      address: ''
    })
  }

  const handleWellnessSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate form
      const amount = wellnessForm.amount === 'custom' ? wellnessForm.customAmount : wellnessForm.amount
      if (!wellnessForm.creatorName || !wellnessForm.creatorPhone) {
        toast.error('Please provide creator name and phone number')
        setIsSubmitting(false)
        return
      }
      if (!amount || parseFloat(amount) <= 0) {
        toast.error('Please select or enter a valid amount')
        setIsSubmitting(false)
        return
      }
      
      if (!wellnessForm.recipientName) {
        toast.error('Please provide recipient name')
        setIsSubmitting(false)
        return
      }

      // Validate based on delivery method
      if (wellnessForm.deliveryMethod === 'digital') {
        if (!wellnessForm.recipientPhone) {
          toast.error('Recipient phone is required for digital delivery')
          setIsSubmitting(false)
          return
        }
      } else if (wellnessForm.deliveryMethod === 'physical') {
        if (!wellnessForm.recipientPhone || !wellnessForm.address) {
          toast.error('Phone and address are required for physical delivery')
          setIsSubmitting(false)
          return
        }
      }
      
      // Sign in anonymously so verify/save functions can run
      const auth = getAuth(app)
      let user = auth.currentUser
      
      if (!user) {
        try {
          const userCredential = await signInAnonymously(auth)
          user = userCredential.user
        } catch (authError) {
          toast.error('Failed to authenticate. Please try again.')
          setIsSubmitting(false)
          return
        }
      }
      
      // Use creator phone number as email for Paystack
      const phoneDigits = wellnessForm.creatorPhone.replace(/\D/g, '')
      const creatorEmail = (phoneDigits || 'customer') + '@temp.sparadiseluxe.com'
      
      // Calculate amount with optional Paystack fee uplift for payment
      const baseAmount = parseFloat(amount)
      const multiplier = applyPaystackFeeUplift ? 1.02 : 1
      const amountWithFee = baseAmount * multiplier
      
      const startPayment = () => {
        if (!window.PaystackPop) {
          toast.error('Payment system not available. Please check your connection and try again.')
          setIsSubmitting(false)
          return
        }
        
        const handler = window.PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: creatorEmail,
          amount: Math.round(amountWithFee * 100), // GHS → pesewas
          currency: 'GHS',
          ref: `WELLNESS_${Date.now()}`,
          metadata: {
            type: 'wellness',
            creatorName: wellnessForm.creatorName,
          },
          callback: function (response) {
            // Payment successful – reuse existing verification + completion flow
            setIsSubmitting(false)
            const ref = response.reference
            setPaymentReference(ref)
            handleCompletePurchase(ref)
          },
          onClose: function () {
            setIsSubmitting(false)
            toast.error('Payment was cancelled. Please try again.')
          },
        })
        
        handler.openIframe()
      }
      
      // If Paystack script already loaded, start immediately
      if (window.PaystackPop) {
        startPayment()
        return
      }
      
      // Otherwise, load Paystack inline script (v1) then start
      const script = document.createElement('script')
      script.src = 'https://js.paystack.co/v1/inline.js'
      script.onload = startPayment
      script.onerror = () => {
        toast.error('Failed to load payment gateway. Please try again.')
        setIsSubmitting(false)
      }
      document.body.appendChild(script)
    } catch (error) {
      console.error('Error starting wellness gift card payment:', error)
      toast.error('An error occurred. Please try again.')
      setIsSubmitting(false)
      resetForm()
    }
  }

  // Verification handler – can use override reference from Paystack callback
  const handleCompletePurchase = async (referenceOverride = null) => {
    const referenceToUse = referenceOverride || paymentReference
    if (!referenceToUse) {
      toast.error('Payment reference not found. Please try again.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
      return
    }

    setIsSubmitting(true)
    setShowCompleteButton(false)

    try {
      // Import Firebase Functions dynamically
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functionsInstance = getFunctions(app)
      
      // Show verification notice
      const verificationToast = toast.loading('Verifying payment status...', {
        style: {
          background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })

      // Wait a moment for Paystack to update transaction status
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Verify payment status with Paystack
      const verifyPayment = httpsCallable(functionsInstance, 'verifyGiftCardPayment')
      const verifyResult = await verifyPayment({
        reference: referenceToUse
      })

      const verifyData = verifyResult.data

      if (!verifyData) {
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        toast.error('Unable to verify payment status. Please check your payment or contact support.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        return
      }

      const status = verifyData.status

      // Check status according to Paystack documentation
      if (status === 'success') {
        // Payment successful! Complete the gift card creation
        toast.dismiss(verificationToast)
        
        // Calculate base amount (without fee)
        const amount = wellnessForm.amount === 'custom' ? wellnessForm.customAmount : wellnessForm.amount
        const baseAmount = parseFloat(amount)
        
        // Save gift card
        const saveGiftCard = httpsCallable(functionsInstance, 'saveGiftCard')
        const saveResult = await saveGiftCard({
          type: 'wellness',
          amount: baseAmount,
          creatorName: wellnessForm.creatorName,
          creatorPhone: wellnessForm.creatorPhone,
          recipientName: wellnessForm.recipientName,
          recipientPhone: wellnessForm.recipientPhone,
          message: wellnessForm.message || '',
          deliveryMethod: wellnessForm.deliveryMethod,
          address: wellnessForm.address || '',
          paymentReference: verifyData.reference,
          creatorEmail: wellnessForm.creatorPhone + '@temp.sparadiseluxe.com'
        })

        const saveData = saveResult.data

        if (!saveData || !saveData.success) {
          toast.error('Failed to save gift card. Please contact support.', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSubmitting(false)
          setShowCompleteButton(true)
          return
        }

        // Send SMS notifications for gift card (no email)
        try {
          const completeFlow = httpsCallable(functionsInstance, 'completeGiftCardFlow')
          await completeFlow({
            giftCardId: saveData.giftCardId
          })
        } catch (smsError) {
          console.error('Error sending SMS notifications:', smsError)
          // Don't fail the whole flow if SMS fails
        }

        toast.success('🎉 Gift card purchased successfully!', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
          iconTheme: {
            primary: '#ffffff',
            secondary: '#9333ea',
          },
        })
        setShowWellnessForm(false)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
      } else if (status === 'abandoned') {
        // Transaction was abandoned
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        
        toast.error('Payment was abandoned. Please try again if you wish to complete the purchase.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      } else if (status === 'failed') {
        // Transaction failed
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        
        toast.error('Payment failed. Please try again or use a different payment method.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      } else if (status === 'pending' || status === 'processing' || status === 'ongoing' || status === 'queued') {
        // Transaction is still processing - wait a bit more and check again
        toast.loading('Payment is processing, please wait...', {
          id: verificationToast,
          style: {
            background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        
        setTimeout(async () => {
          try {
            const verifyResult2 = await verifyPayment({
              reference: paymentReference
            })
            const verifyData2 = verifyResult2.data
            
            if (verifyData2 && verifyData2.status === 'success') {
              toast.dismiss(verificationToast)
              // Recursively call handleCompletePurchase to complete the flow
              setPaymentReference(paymentReference)
              await handleCompletePurchase()
            } else {
              // Still pending or failed - treat as abandoned after waiting
              toast.dismiss(verificationToast)
              setIsSubmitting(false)
              setShowCompleteButton(false)
              setPaymentReference(null)
              resetForm()
              setShowWellnessForm(false) // Close form automatically
              toast.error('Payment status unclear. Please check your payment or contact support.', {
                duration: 5000,
                style: {
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: '600',
                },
              })
            }
          } catch (error) {
            console.error('Error re-verifying payment:', error)
            toast.dismiss(verificationToast)
            setIsSubmitting(false)
            setShowCompleteButton(false)
            setPaymentReference(null)
            resetForm()
            setShowWellnessForm(false) // Close form automatically
            toast.error('Unable to verify payment status. Please check your payment or contact support.', {
              duration: 5000,
              style: {
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                color: '#ffffff',
                border: '2px solid #ffffff',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
              },
            })
          }
        }, 3000) // Wait 3 more seconds for status to update
      } else if (status === 'reversed') {
        // Transaction was reversed/refunded
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        toast.error('Payment was reversed. Please contact support for assistance.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      } else {
        // Unknown status
        toast.dismiss(verificationToast)
        setIsSubmitting(false)
        setShowCompleteButton(false)
        setPaymentReference(null)
        resetForm()
        setShowWellnessForm(false) // Close form automatically
        toast.error('Payment status unclear. Please check your payment or contact support.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      }
    } catch (error) {
      console.error('Error verifying payment:', error)
      setIsSubmitting(false)
      setShowCompleteButton(false)
      setPaymentReference(null)
      resetForm()
      setShowWellnessForm(false) // Close form automatically
      toast.error('Unable to verify payment status. Please check your payment or contact support.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
    }
  }

  const handleWellnessFormChange = (field, value) => {
    setWellnessForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const [showSessionForm, setShowSessionForm] = useState(false)
  const [sessionForm, setSessionForm] = useState({
    category: '',
    type: '',
    amount: '',
    spaPrice: '',
    mobilePrice: '',
    duration: '',
    visitType: 'spa',
    people: 1,
    baseAmount: '',
    creatorName: '',
    creatorPhone: '',
    recipientName: '',
    recipientPhone: '',
    message: '',
    deliveryMethod: 'digital',
    address: '' // For physical delivery
  })
  const [isSessionSubmitting, setIsSessionSubmitting] = useState(false)
  const [sessionPaymentReference, setSessionPaymentReference] = useState(null)
  const [showSessionCompleteButton, setShowSessionCompleteButton] = useState(false)

  // Resolve session prices from Firestore by category+type so URL cannot be tampered to change price
  const fetchServiceByCategoryAndType = async (categoryKey, typeName) => {
    try {
      if (categoryKey === 'Combo Packages') {
        const combosRef = collection(db, 'comboPackages')
        const q = query(combosRef, where('isActive', '==', true))
        const snap = await getDocs(q)
        const service = snap.docs.map(d => ({ id: d.id, ...d.data() })).find(s => (s.name || '').trim() === (typeName || '').trim())
        return service || null
      }
      const servicesRef = collection(db, 'services')
      const q = query(
        servicesRef,
        where('isActive', '==', true),
        where('category', '==', categoryKey)
      )
      const snap = await getDocs(q)
      const service = snap.docs.map(d => ({ id: d.id, ...d.data() })).find(s => (s.name || '').trim() === (typeName || '').trim())
      return service || null
    } catch (e) {
      console.error('Failed to fetch service for gift card:', e)
      return null
    }
  }

  // Check URL parameters when component mounts or location changes; resolve prices from Firestore only
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const category = (params.get('category') || '').trim()
    const typeName = (params.get('type') || '').trim()
    const showForm = params.get('showSessionForm') === 'true' || (category && typeName)

    if (!showForm || !category || !typeName) {
      setShowSessionForm(false)
      return
    }

    let cancelled = false
    ;(async () => {
      const service = await fetchServiceByCategoryAndType(category, typeName)
      if (cancelled) return
      if (!service) {
        setShowSessionForm(true)
        setSessionForm(prev => ({
          ...prev,
          category,
          type: typeName,
          amount: '',
          spaPrice: '',
          mobilePrice: '',
          duration: params.get('duration') || '',
          visitType: 'spa',
          people: 1,
          baseAmount: ''
        }))
        toast.error('Service not found. Please select a session from the Services page.')
        return
      }
      const spa = Number(service.spaPrice ?? service.price ?? service.amount ?? 0) || 0
      const mobile = Number(service.mobilePrice ?? service.price ?? service.amount ?? 0) || 0
      const effectiveDiscount = promoDiscount > 0 ? promoDiscount : (Number(service.discount) || 0)
      const finalSpa = effectiveDiscount > 0 ? spa * (1 - effectiveDiscount / 100) : spa
      const finalMobile = effectiveDiscount > 0 ? mobile * (1 - effectiveDiscount / 100) : mobile
      const duration = String(service.duration ?? params.get('duration') ?? '')

      // Choose initial visit type based on allowed visit types from admin settings
      const initialVisitType = (() => {
        if (allowedVisitTypes.includes('spa')) return 'spa'
        if (allowedVisitTypes.includes('mobile')) return 'mobile'
        return 'spa'
      })()

      const perPersonPrice = initialVisitType === 'spa' ? finalSpa : finalMobile
      const people = 1
      const baseAmount = perPersonPrice * people
      const amountWithFee = baseAmount * (applyPaystackFeeUplift ? 1.02 : 1)
      setSessionForm(prev => ({
        ...prev,
        category,
        type: typeName,
        amount: amountWithFee.toFixed(2),
        spaPrice: finalSpa.toFixed(2),
        mobilePrice: finalMobile.toFixed(2),
        duration,
        visitType: initialVisitType,
        people,
        baseAmount: baseAmount.toFixed(2)
      }))
      setShowSessionForm(true)
    })()
    return () => { cancelled = true }
  }, [location.search, applyPaystackFeeUplift, promoDiscount, allowedVisitTypes])

  // Function to reset session form
  const resetSessionForm = () => {
    setSessionForm(prev => ({
      ...prev,
      creatorName: '',
      creatorPhone: '',
      recipientName: '',
      recipientPhone: '',
      message: '',
      deliveryMethod: 'digital',
      address: ''
    }))
  }

  const handleSessionChange = (field, value) => {
    setSessionForm(prev => {
      // When visit type or people change, recalculate base amount and amount with fee
      if (field === 'visitType' || field === 'people') {
        const visitType = field === 'visitType' ? value : prev.visitType
        const people = field === 'people' ? Number(value) || 1 : Number(prev.people) || 1
        const perPersonPrice =
          visitType === 'spa'
            ? Number(prev.spaPrice) || 0
            : Number(prev.mobilePrice) || 0
        const baseAmount = perPersonPrice * people
        const amountWithFee = baseAmount * (applyPaystackFeeUplift ? 1.02 : 1)
        return {
          ...prev,
          visitType,
          people,
          baseAmount: baseAmount.toFixed(2),
          amount: amountWithFee.toFixed(2),
        }
      }
      return { ...prev, [field]: value }
    })
  }

  const handleSessionSubmit = async (e) => {
    e.preventDefault()
    setIsSessionSubmitting(true)

    try {
      // Validate form
      if (!sessionForm.creatorName || !sessionForm.creatorPhone) {
        toast.error('Please provide creator name and phone number', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        setIsSessionSubmitting(false)
        return
      }

      if (!sessionForm.recipientName) {
        toast.error('Please provide recipient name', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        setIsSessionSubmitting(false)
        return
      }

      // Validate delivery method requirements
      if (sessionForm.deliveryMethod === 'digital') {
        if (!sessionForm.recipientPhone) {
          toast.error('Digital delivery requires recipient phone number', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSessionSubmitting(false)
          return
        }
      } else if (sessionForm.deliveryMethod === 'physical') {
        if (!sessionForm.recipientPhone || !sessionForm.address) {
          toast.error('Physical delivery requires recipient phone number and delivery address', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSessionSubmitting(false)
          return
        }
      }

      // Authenticate user (required for verify/save functions)
      const auth = getAuth(app)
      await signInAnonymously(auth)

        // amountToPay includes 2% charges fee when applied (computed from baseAmount × 1.02)
        const amountToPay = parseFloat(sessionForm.amount)
      
      // Use creator phone as email (format: phone@temp.sparadiseluxe.com)
      const phoneDigits = sessionForm.creatorPhone.replace(/\D/g, '')
      const creatorEmail = (phoneDigits || 'customer') + '@temp.sparadiseluxe.com'

      const startPayment = () => {
        if (!window.PaystackPop) {
          toast.error('Payment system not available. Please check your connection and try again.', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSessionSubmitting(false)
          return
        }

        const handler = window.PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: creatorEmail,
          amount: Math.round(amountToPay * 100), // GHS → pesewas
          currency: 'GHS',
          ref: `SESSION_${Date.now()}`,
          metadata: {
            type: 'session',
            category: sessionForm.category,
            serviceType: sessionForm.type,
            creatorName: sessionForm.creatorName,
          },
          callback: function (response) {
            // Payment successful – verify and complete session gift card
            setIsSessionSubmitting(false)
            const ref = response.reference
            setSessionPaymentReference(ref)
            handleCompleteSessionPurchase(ref)
          },
          onClose: function () {
            setIsSessionSubmitting(false)
            toast.error('Payment was cancelled. Please try again.', {
              duration: 5000,
              style: {
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#ffffff',
                border: '2px solid #ffffff',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
              },
            })
          },
        })

        handler.openIframe()
      }

      // If Paystack script already loaded, start immediately
      if (window.PaystackPop) {
        startPayment()
        return
      }

      // Otherwise, load Paystack inline script (v1) then start
      const script = document.createElement('script')
      script.src = 'https://js.paystack.co/v1/inline.js'
      script.onload = startPayment
      script.onerror = () => {
        toast.error('Failed to load payment gateway. Please try again.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        setIsSessionSubmitting(false)
      }
      document.body.appendChild(script)
    } catch (error) {
      console.error('Error initializing payment:', error)
      toast.error('An error occurred. Please try again.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
      setIsSessionSubmitting(false)
      resetSessionForm()
    }
  }

  // Verification handler for session gift card (also used for automatic inline confirmation)
  const handleCompleteSessionPurchase = async (referenceOverride = null) => {
    const referenceToUse = referenceOverride || sessionPaymentReference
    if (!referenceToUse) {
      toast.error('Payment reference not found. Please try again.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
      return
    }

    setIsSessionSubmitting(true)
    setShowSessionCompleteButton(false)

    try {
      // Import Firebase Functions dynamically
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const functionsInstance = getFunctions(app)

      // Show verification notice
      const verificationToast = toast.loading('Verifying payment status...', {
        style: {
          background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })

      // Wait a moment for Paystack to update transaction status
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Verify payment status with Paystack
      const verifyPayment = httpsCallable(functionsInstance, 'verifyGiftCardPayment')
      const verifyResult = await verifyPayment({
        reference: referenceToUse
      })

      const verifyData = verifyResult.data

      if (!verifyData) {
        toast.dismiss(verificationToast)
        setIsSessionSubmitting(false)
        setShowSessionCompleteButton(false)
        setSessionPaymentReference(null)
        resetSessionForm()
        setShowSessionForm(false)
        toast.error('Unable to verify payment status. Please check your payment or contact support.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
        return
      }

      const status = verifyData.status

      // Check status according to Paystack documentation
      if (status === 'success') {
        // Payment successful! Complete the gift card creation
        toast.dismiss(verificationToast)

        // Base amount for the session value (without charges fee uplift)
        const baseAmount = parseFloat(sessionForm.baseAmount || sessionForm.amount) || 0
        const spaBase = parseFloat(sessionForm.spaPrice || '0') || 0
        const mobileBase = parseFloat(sessionForm.mobilePrice || '0') || 0

        // Save gift card
        const saveGiftCard = httpsCallable(functionsInstance, 'saveGiftCard')
        const saveResult = await saveGiftCard({
          type: 'session',
          sessionCategory: sessionForm.category,
          serviceType: sessionForm.type,
          duration: parseInt(sessionForm.duration),
          price: baseAmount, // Session card value (without charges fee uplift)
          amount: baseAmount,
          spaPrice: spaBase,
          mobilePrice: mobileBase,
          people: Number(sessionForm.people) || 1,
          visitType: sessionForm.visitType || 'spa',
          creatorName: sessionForm.creatorName,
          creatorPhone: sessionForm.creatorPhone,
          recipientName: sessionForm.recipientName,
          recipientPhone: sessionForm.recipientPhone,
          message: sessionForm.message || '',
          deliveryMethod: sessionForm.deliveryMethod,
          address: sessionForm.address || '',
          paymentReference: verifyData.reference,
          creatorEmail: sessionForm.creatorPhone + '@temp.sparadiseluxe.com'
        })

        const saveData = saveResult.data

        if (!saveData || !saveData.success) {
          toast.error('Failed to save gift card. Please contact support.', {
            duration: 5000,
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: '2px solid #ffffff',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '16px',
              fontWeight: '600',
            },
          })
          setIsSessionSubmitting(false)
          setShowSessionCompleteButton(true)
          return
        }

        // Send SMS notifications for gift card (no email)
        try {
          const completeFlow = httpsCallable(functionsInstance, 'completeGiftCardFlow')
          await completeFlow({
            giftCardId: saveData.giftCardId
          })
        } catch (smsError) {
          console.error('Error sending SMS notifications:', smsError)
          // Don't fail the whole flow if SMS fails
        }

        toast.success('🎉 Gift card purchased successfully!', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
          iconTheme: {
            primary: '#ffffff',
            secondary: '#9333ea',
          },
        })
        setShowSessionForm(false)
        setIsSessionSubmitting(false)
        setShowSessionCompleteButton(false)
        setSessionPaymentReference(null)
        resetSessionForm()
      } else if (status === 'abandoned' || status === 'failed' || status === 'reversed') {
        // Transaction was abandoned/failed/reversed
        toast.dismiss(verificationToast)
        setIsSessionSubmitting(false)
        setShowSessionCompleteButton(false)
        setSessionPaymentReference(null)
        resetSessionForm()
        setShowSessionForm(false)

        const errorMessages = {
          abandoned: 'Payment was abandoned. Please try again if you wish to complete the purchase.',
          failed: 'Payment failed. Please try again or use a different payment method.',
          reversed: 'Payment was reversed. Please contact support for assistance.'
        }

        toast.error(errorMessages[status] || 'Payment was unsuccessful. Please try again.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      } else {
        // Unknown or pending status
        toast.dismiss(verificationToast)
        setIsSessionSubmitting(false)
        setShowSessionCompleteButton(false)
        setSessionPaymentReference(null)
        resetSessionForm()
        setShowSessionForm(false)
        toast.error('Payment status unclear. Please check your payment or contact support.', {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            color: '#ffffff',
            border: '2px solid #ffffff',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
          },
        })
      }
    } catch (error) {
      console.error('Error verifying payment:', error)
      setIsSessionSubmitting(false)
      setShowSessionCompleteButton(false)
      setSessionPaymentReference(null)
      resetSessionForm()
      setShowSessionForm(false)
      toast.error('Unable to verify payment status. Please check your payment or contact support.', {
        duration: 5000,
        style: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          color: '#ffffff',
          border: '2px solid #ffffff',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
        },
      })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      {/* Hero Section */}
      <div
        className="relative text-white py-14 md:py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/media/images/hero/image5.png)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        <div className="container-spa max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-full mb-4 shadow-lg">
            <Gift className="w-8 h-8 md:w-10 md:h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-3 drop-shadow-lg">Gift Cards</h1>
          <p className="text-lg md:text-xl text-white/95 max-w-2xl mx-auto drop-shadow-md">
            Check your balance, buy a session or wellness gift card. Perfect for treating yourself or someone special.
          </p>
        </div>
      </div>

      <div className="container-spa max-w-4xl mx-auto px-4 -mt-2 pb-16">

        {/* Gift Card Validation Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-8 md:p-10 mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-800 mb-6 text-center">
            Check Gift Card Validity
          </h2>
          
          <form onSubmit={handleValidate} className="space-y-6">
            <div>
              <label htmlFor="giftCardCode" className="block text-sm font-medium text-gray-700 mb-2">
                Enter Gift Card Code
              </label>
              <input
                type="text"
                id="giftCardCode"
                value={giftCardCode}
                onChange={(e) => {
                  setGiftCardCode(e.target.value.toUpperCase())
                  setValidationResult(null)
                }}
                placeholder="Enter your gift card code"
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all duration-200 text-lg font-mono tracking-wider"
                maxLength={20}
              />
              <p className="mt-2 text-sm text-gray-500">
                Enter the code found on your gift card
              </p>
            </div>

            <button
              type="submit"
              disabled={isValidating || !giftCardCode.trim()}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isValidating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Validating...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Validate Gift Card</span>
                </>
              )}
            </button>
          </form>

          {/* Validation Result */}
          {validationResult && (
            <div className={`mt-6 p-6 rounded-xl border-2 ${
              validationResult.valid 
                ? 'bg-purple-50 border-purple-300' 
                : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-start space-x-3">
                {validationResult.valid ? (
                  <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-semibold text-lg ${
                    validationResult.valid ? 'text-purple-800' : 'text-red-800'
                  }`}>
                    {validationResult.message}
                  </p>
                  
                  {validationResult.valid && validationResult.cardDetails && (
                    <div className="mt-4 space-y-3">
                      <div className="bg-white/60 rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700 font-medium">Card Type:</span>
                          <span className="text-gray-900 font-semibold">
                            {validationResult.type === 'session' ? 'Session Gift Card' : 'Wellness Gift Card'}
                          </span>
                        </div>
                        {validationResult.balance && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-700 font-medium">Available Balance:</span>
                            <span className="text-purple-700 font-bold text-lg">
                              {validationResult.balance}
                            </span>
                          </div>
                        )}
                        {validationResult.expiryDate && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-700 font-medium">Expiry Date:</span>
                            <span className="text-gray-900 font-semibold">
                              {validationResult.expiryDate}
                            </span>
                          </div>
                        )}
                        {validationResult.type === 'session' && validationResult.cardDetails && (
                          <>
                            {validationResult.cardDetails.visitType && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-700 font-medium">Visit Type:</span>
                                <span className="text-gray-900 font-semibold capitalize">
                                  {validationResult.cardDetails.visitType === 'mobile' ? 'Mobile Spa' : validationResult.cardDetails.visitType === 'spa' ? 'Spa' : validationResult.cardDetails.visitType}
                                </span>
                              </div>
                            )}
                            {(validationResult.cardDetails.people != null && validationResult.cardDetails.people !== '') && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-700 font-medium">People:</span>
                                <span className="text-gray-900 font-semibold">
                                  {validationResult.cardDetails.people}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Usage Instructions */}
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mt-4">
                        <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                          <Info className="w-5 h-5 mr-2" />
                          How to Use Your Gift Card
                        </h4>
                        <div className="space-y-2 text-sm text-purple-800">
                          {validationResult.type === 'session' ? (
                            <>
                              <p className="flex items-start">
                                <span className="mr-2">1.</span>
                                <span>
                                  Tap{" "}
                                  {sessionBookingLink ? (
                                    <Link
                                      to={sessionBookingLink}
                                      className="text-purple-600 font-semibold underline hover:text-purple-800"
                                    >
                                      Book this session
                                    </Link>
                                  ) : (
                                    <span className="font-semibold">Book this session</span>
                                  )}{" "}
                                  below to open the booking form prefilled with this treatment and your gift card.
                                </span>
                              </p>
                              <p className="flex items-start">
                                <span className="mr-2">2.</span>
                                <span>Choose your visit type (Spa or Mobile) and preferred date/time.</span>
                              </p>
                              <p className="flex items-start">
                                <span className="mr-2">3.</span>
                                <span>Fill in your contact details and confirm the booking.</span>
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="flex items-start">
                                <span className="mr-2">1.</span>
                                <span>Go to <Link to="/services" className="text-purple-600 font-semibold underline hover:text-purple-800">Services</Link> page to view all available services</span>
                              </p>
                              <p className="flex items-start">
                                <span className="mr-2">2.</span>
                                <span>Select any service category and service type</span>
                              </p>
                              <p className="flex items-start">
                                <span className="mr-2">3.</span>
                                <span>Click "Book Now" and check the "I have a gift card" option during booking</span>
                              </p>
                              <p className="flex items-start">
                                <span className="mr-2">4.</span>
                                <span>Enter your gift card code when prompted</span>
                              </p>
                              <p className="flex items-start">
                                <span className="mr-2">5.</span>
                                <span>The gift card balance will be applied to your booking. If the service cost exceeds the balance, you can pay the difference.</span>
                              </p>
                            </>
                          )}
                        </div>
                        <div className="mt-4 pt-3 border-t border-purple-200">
                          {validationResult.type === 'session' && sessionBookingLink ? (
                            <Link 
                              to={sessionBookingLink}
                              className="inline-flex items-center text-purple-700 font-semibold hover:text-purple-900 transition-colors"
                            >
                              <span>Book This Session With Your Gift Card</span>
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                          ) : (
                            <Link 
                              to="/services" 
                              className="inline-flex items-center text-purple-700 font-semibold hover:text-purple-900 transition-colors"
                            >
                              <span>Go to Services Page</span>
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {!validationResult.valid && validationResult.expiryDate && (
                    <div className="mt-3">
                      <p className="text-red-700 text-sm">
                        <span className="font-medium">Expiry Date:</span> {validationResult.expiryDate}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
          <Link
            to="/services"
            className="relative bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-5 px-6 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center space-x-3 text-lg"
          >
            <Gift className="w-6 h-6" />
            <span>Buy Session Gift Card</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
          <button
            onClick={() => setShowWellnessForm(true)}
            className="relative bg-gradient-to-r from-purple-400 to-pink-400 hover:from-purple-500 hover:to-pink-500 text-white font-semibold py-5 px-6 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center space-x-3 text-lg border-2 border-purple-200/50"
          >
            <Sparkles className="w-6 h-6" />
            <span>Buy Wellness Gift Card</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Learn About Gift Cards */}
        <div className="mb-8 text-center">
          <Link
            to="/terms"
            className="inline-flex items-center space-x-3 bg-white border-2 border-purple-200 text-purple-700 hover:bg-purple-50 font-semibold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
          >
            <Info className="w-5 h-5" />
            <span>Learn About Gift Cards</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Info Section */}
        <div className="mt-10 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-200">
          <h3 className="text-xl font-serif font-semibold text-gray-800 mb-4">
            Gift Card Information
          </h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Valid for 90 days (3 months) from the date of purchase</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Session Gift Cards: Redeemable for the specific treatment purchased</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Wellness Gift Cards: Can be used for any spa service or multiple sessions</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Perfect for birthdays, anniversaries, or any special occasion</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Digital delivery available instantly via SMS or WhatsApp</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <span>Physical card delivery option available (terms apply)</span>
            </li>
          </ul>
        </div>

        {/* Gift Card Templates Section */}
        <div className="mt-16">
          <h2 className="text-3xl font-serif font-bold text-gray-800 mb-8 text-center">
            Gift Card Preview
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Preview how your gift cards will look when delivered digitally or physically
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 max-w-4xl mx-auto">
            {/* Session Gift Card Template */}
            <div className="relative">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Session Gift Card Template
              </h3>
              <div className="relative max-w-sm mx-auto" style={{ aspectRatio: '16/10' }}>
                {/* Flip Card Container */}
                <div className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${flippedCard === 'session' ? 'rotate-y-180' : ''}`}>
                  {/* Front Side */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300 backface-hidden">
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src="/media/images/logo/lumoralogo.png"
                        alt="Gift Card Background"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                      {/* Overlay for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/50"></div>
                    </div>
                    
                    {/* Card Content */}
                    <div className="relative h-full flex flex-col justify-between p-4 md:p-5 text-white">
                      {/* Top Section - Logo and Brand */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <img
                            src="/media/images/logo/lumoralogo.png"
                            alt="Spa~Radise Luxe"
                            className="w-8 h-8 md:w-10 md:h-10 object-contain bg-white/20 rounded-full p-1"
                          />
                          <div>
                            <h4 className="text-sm md:text-base font-serif font-bold">Spa~Radise Luxe</h4>
                            <p className="text-xs text-white/90">Gift Card</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-white/80">Gift Card</div>
                          <div className="text-xs text-white/70">Session Type</div>
                        </div>
                      </div>

                      {/* Middle Section - Service Details */}
                      <div className="flex-1 flex flex-col justify-center">
                        <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 md:p-3 mb-2">
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-white/80">Category:</span>
                              <span className="text-xs text-white/70 font-semibold">Massage</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-white/80">Service:</span>
                              <span className="text-xs md:text-sm font-serif font-bold">Swedish Massage</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-white/70">60 minutes</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Section - Instructions */}
                      <div className="bg-white/15 backdrop-blur-sm rounded-lg p-1.5 md:p-2">
                        <p className="text-xs text-white/80 mb-0.5 font-semibold">To redeem:</p>
                        <p className="text-xs text-white/70 leading-tight">Visit sparadiseluxe.com/gift-card</p>
                        <p className="text-xs text-white/70 leading-tight">Enter your card code to validate and book</p>
                      </div>
                    </div>
                    
                    {/* Flip Button */}
                    <button
                      onClick={() => setFlippedCard(flippedCard === 'session' ? null : 'session')}
                      className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/20 backdrop-blur-sm rounded-full p-1.5 hover:bg-white/30 transition-all z-10"
                      title="Flip card"
                    >
                      <RotateCw className="w-3 h-3 text-white" />
                    </button>
                  </div>

                  {/* Back Side */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl transform rotate-y-180 backface-hidden">
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src="/media/images/logo/lumoralogo.png"
                        alt="Gift Card Back Background"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                      {/* Overlay for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/60"></div>
                    </div>
                    
                    {/* Card Content - Back */}
                    <div className="relative h-full flex flex-col justify-between p-4 md:p-5 text-white">
                      {/* Top Section - Logo */}
                      <div className="flex justify-center mb-2">
                        <img
                          src="/media/images/logo/lumoralogo.png"
                          alt="Spa~Radise Luxe"
                          className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/20 rounded-full p-1.5"
                        />
                      </div>

                      {/* Middle Section - Card Details */}
                      <div className="flex-1 flex flex-col justify-center">
                        <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 md:p-3 text-center">
                          <p className="text-xs text-white/80 mb-1">Card Number</p>
                          <p className="text-xs md:text-sm font-mono font-bold tracking-wider mb-3 break-all">XXXX-XXXX-XXXX-XXXX</p>
                          
                          <div className="border-t border-white/30 pt-2">
                            <p className="text-xs text-white/80 mb-0.5">Expiry Date</p>
                            <p className="text-xs md:text-sm font-semibold">XX/XX/XXXX</p>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Section - Empty for balance */}
                      <div className="h-4"></div>
                    </div>
                    
                    {/* Flip Button */}
                    <button
                      onClick={() => setFlippedCard(null)}
                      className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/20 backdrop-blur-sm rounded-full p-1.5 hover:bg-white/30 transition-all z-10"
                      title="Flip card"
                    >
                      <RotateCw className="w-3 h-3 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Wellness Gift Card Template */}
            <div className="relative">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Wellness Gift Card Template
              </h3>
              <div className="relative max-w-sm mx-auto" style={{ aspectRatio: '16/10' }}>
                {/* Flip Card Container */}
                <div className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d ${flippedCard === 'wellness' ? 'rotate-y-180' : ''}`}>
                  {/* Front Side */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300 backface-hidden">
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src="/media/images/logo/lumoralogo.png"
                        alt="Gift Card Background"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                      {/* Overlay for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/50"></div>
                    </div>
                    
                    {/* Card Content */}
                    <div className="relative h-full flex flex-col justify-between p-4 md:p-5 text-white">
                      {/* Top Section - Logo and Brand */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <img
                            src="/media/images/logo/lumoralogo.png"
                            alt="Spa~Radise Luxe"
                            className="w-8 h-8 md:w-10 md:h-10 object-contain bg-white/20 rounded-full p-1"
                          />
                          <div>
                            <h4 className="text-sm md:text-base font-serif font-bold">Spa~Radise Luxe</h4>
                            <p className="text-xs text-white/90">Wellness</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-white/80">Gift Card</div>
                          <div className="text-xs text-white/70">Wellness</div>
                        </div>
                      </div>

                      {/* Middle Section - Amount */}
                      <div className="flex-1 flex flex-col justify-center items-center">
                        <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 md:p-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-xs text-white/80">Gift Card Value</span>
                            <h5 className="text-sm md:text-base font-serif font-bold">GHS XXXX</h5>
                            <span className="text-xs text-white/70">Use for any spa service</span>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Section - Instructions */}
                      <div className="bg-white/15 backdrop-blur-sm rounded-lg p-1.5 md:p-2">
                        <p className="text-xs text-white/80 mb-0.5 font-semibold">To redeem:</p>
                        <p className="text-xs text-white/70 leading-tight">Visit sparadiseluxe.com/gift-card</p>
                        <p className="text-xs text-white/70 leading-tight">Enter your card code to validate and book</p>
                      </div>
                    </div>
                    
                    {/* Flip Button */}
                    <button
                      onClick={() => setFlippedCard(flippedCard === 'wellness' ? null : 'wellness')}
                      className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/20 backdrop-blur-sm rounded-full p-1.5 hover:bg-white/30 transition-all z-10"
                      title="Flip card"
                    >
                      <RotateCw className="w-3 h-3 text-white" />
                    </button>
                  </div>

                  {/* Back Side */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl transform rotate-y-180 backface-hidden">
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src="/media/images/logo/lumoralogo.png"
                        alt="Gift Card Back Background"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                      {/* Overlay for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/60"></div>
                    </div>
                    
                    {/* Card Content - Back */}
                    <div className="relative h-full flex flex-col justify-between p-4 md:p-5 text-white">
                      {/* Top Section - Logo */}
                      <div className="flex justify-center mb-2">
                        <img
                          src="/media/images/logo/lumoralogo.png"
                          alt="Spa~Radise Luxe"
                          className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/20 rounded-full p-1.5"
                        />
                      </div>

                      {/* Middle Section - Card Details */}
                      <div className="flex-1 flex flex-col justify-center">
                        <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2 md:p-3 text-center">
                          <p className="text-xs text-white/80 mb-1">Card Number</p>
                          <p className="text-xs md:text-sm font-mono font-bold tracking-wider mb-3 break-all">XXXX-XXXX-XXXX-XXXX</p>
                          
                          <div className="border-t border-white/30 pt-2">
                            <p className="text-xs text-white/80 mb-0.5">Expiry Date</p>
                            <p className="text-xs md:text-sm font-semibold">XX/XX/XXXX</p>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Section - Empty for balance */}
                      <div className="h-4"></div>
                    </div>
                    
                    {/* Flip Button */}
                    <button
                      onClick={() => setFlippedCard(null)}
                      className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/20 backdrop-blur-sm rounded-full p-1.5 hover:bg-white/30 transition-all z-10"
                      title="Flip card"
                    >
                      <RotateCw className="w-3 h-3 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="text-center text-gray-600 text-sm max-w-2xl mx-auto">
            <p className="mb-2">
              <strong>Note:</strong> These are template designs. Actual gift cards will include your unique card number, 
              expiry date, and specific service details (for Session Gift Cards) or amount (for Wellness Gift Cards).
            </p>
            <p>
              Cards can be delivered digitally via email/SMS/WhatsApp or as physical cards.
            </p>
          </div>
        </div>
      </div>

      {/* Legal modal for Terms / Privacy (non-blocking) */}
      {legalModalType && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-800">
                {legalModalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </h2>
              <button
                type="button"
                onClick={() => setLegalModalType(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="p-4">
                {legalModalType === 'terms' ? <Terms /> : <Privacy />}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setLegalModalType(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

          {/* Wellness Gift Card Form Modal */}
          {showWellnessForm && createPortal(
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" style={{ zIndex: 100000 }}>
                {/* Modal Header */}
                <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Sparkles className="w-6 h-6" />
                    <h2 className="text-2xl font-serif font-bold">Spa~Radise Luxe Wellness Gift Card</h2>
                  </div>
                  <button
                    onClick={() => {
                      setShowWellnessForm(false)
                      setShowCompleteButton(false)
                      setPaymentReference(null)
                      if (!isSubmitting) {
                        resetForm()
                      }
                    }}
                    className="text-white hover:text-gray-200 transition-colors"
                    disabled={isSubmitting}
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Form Content */}
                <form onSubmit={handleWellnessSubmit} className="p-6 md:p-8 space-y-6">
                  {/* Amount Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Gift Card Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                      {['200', '300', '500', '750', '1000'].map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => handleWellnessFormChange('amount', amount)}
                          className={`py-3 px-4 rounded-lg border-2 transition-all ${
                            wellnessForm.amount === amount
                              ? 'border-purple-500 bg-purple-50 text-purple-700 font-semibold'
                              : 'border-gray-300 hover:border-purple-300 text-gray-700'
                          }`}
                        >
                          GHS {amount}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleWellnessFormChange('amount', 'custom')}
                        className={`py-3 px-4 rounded-lg border-2 transition-all ${
                          wellnessForm.amount === 'custom'
                            ? 'border-purple-500 bg-purple-50 text-purple-700 font-semibold'
                            : 'border-gray-300 hover:border-purple-300 text-gray-700'
                        }`}
                      >
                        Custom
                      </button>
                    </div>
                    {wellnessForm.amount === 'custom' && (
                      <input
                        type="number"
                        min="100"
                        step="10"
                        value={wellnessForm.customAmount}
                        onChange={(e) => handleWellnessFormChange('customAmount', e.target.value)}
                        placeholder="Enter custom amount (min. GHS 100)"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                      />
                    )}
                  </div>

                  {/* Creator Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Creator Name <span className="text-red-500">*</span></label>
                      <input type="text" required value={wellnessForm.creatorName} onChange={(e)=>handleWellnessFormChange('creatorName', e.target.value)} placeholder="Your full name" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Creator Phone <span className="text-red-500">*</span></label>
                      <input type="tel" required value={wellnessForm.creatorPhone} onChange={(e)=>handleWellnessFormChange('creatorPhone', e.target.value)} placeholder="024 XXX XXXX" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all" />
                    </div>
                  </div>

                  {/* Recipient Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                      <Heart className="w-5 h-5 text-pink-500" />
                      <span>Recipient Details</span>
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recipient Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={wellnessForm.recipientName}
                        onChange={(e) => handleWellnessFormChange('recipientName', e.target.value)}
                        placeholder="Enter recipient's full name"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                      />
                    </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Phone Number
                    </label>
                    <input
                      type="tel"
                      value={wellnessForm.recipientPhone}
                      onChange={(e) => handleWellnessFormChange('recipientPhone', e.target.value)}
                      placeholder="+233 XX XXX XXXX"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                    />
                  </div>
                    <p className="text-xs text-gray-500">
                      * At least one contact method (Email or Phone) is required
                    </p>
                  </div>

                  {/* Optional Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Personal Message (Optional)
                    </label>
                    <textarea
                      value={wellnessForm.message}
                      onChange={(e) => handleWellnessFormChange('message', e.target.value)}
                      placeholder="Add a personal message for the recipient..."
                      rows={4}
                      maxLength={200}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {wellnessForm.message.length}/200 characters
                    </p>
                  </div>

                  {/* Delivery Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Delivery Method
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-400 transition-all">
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="digital"
                          checked={wellnessForm.deliveryMethod === 'digital'}
                          onChange={(e) => handleWellnessFormChange('deliveryMethod', e.target.value)}
                          className="w-4 h-4 text-green-500"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">Digital Delivery</div>
                          <div className="text-sm text-gray-600">Sent instantly via SMS or WhatsApp</div>
                        </div>
                      </label>
                      <label className="flex items-center space-x-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-400 transition-all">
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="physical"
                          checked={wellnessForm.deliveryMethod === 'physical'}
                          onChange={(e) => handleWellnessFormChange('deliveryMethod', e.target.value)}
                          className="w-4 h-4 text-green-500"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">Physical Card</div>
                          <div className="text-sm text-gray-600">Delivered within 24-48 hours (delivery fee applies)</div>
                        </div>
                      </label>
                    </div>
                    
                    {/* Address field for physical delivery */}
                    {wellnessForm.deliveryMethod === 'physical' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Delivery Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          required={wellnessForm.deliveryMethod === 'physical'}
                          value={wellnessForm.address}
                          onChange={(e) => handleWellnessFormChange('address', e.target.value)}
                          placeholder="Enter the full delivery address (street, city, region)"
                          rows={3}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all resize-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Required for physical card delivery
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Terms Notice */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      By proceeding, you agree to our{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModalType('terms')}
                        className="text-blue-900 underline font-semibold hover:text-blue-950"
                      >
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModalType('privacy')}
                        className="text-blue-900 underline font-semibold hover:text-blue-950"
                      >
                        Privacy Policy
                      </button>.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowWellnessForm(false)
                        setShowCompleteButton(false)
                        setPaymentReference(null)
                        if (!isSubmitting) {
                          resetForm()
                        }
                      }}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <Gift className="w-5 h-5" />
                          <span>Proceed to Payment</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}

          {showSessionForm && createPortal(
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" style={{ zIndex: 100000 }}>
                {/* Modal Header */}
                <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Heart className="w-6 h-6" />
                    <h2 className="text-2xl font-serif font-bold">Session Gift Card</h2>
                  </div>
                  <button
                    onClick={() => {
                      setShowSessionForm(false)
                      setShowSessionCompleteButton(false)
                      setSessionPaymentReference(null)
                      if (!isSessionSubmitting) {
                        resetSessionForm()
                      }
                    }}
                    className="text-white hover:text-gray-200 transition-colors"
                    disabled={isSessionSubmitting}
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSessionSubmit} className="p-6 md:p-8 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <input className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100" readOnly value={sessionForm.category} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
                      <input className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100" readOnly value={sessionForm.type} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Visit Type for Recipient</label>
                      <select
                        value={sessionForm.visitType}
                        onChange={e => handleSessionChange('visitType', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all bg-white"
                      >
                        {allowedVisitTypes.includes('spa') && (
                          <option value="spa">Spa (come to our spa)</option>
                        )}
                        {allowedVisitTypes.includes('mobile') && (
                          <option value="mobile">Mobile Spa (we come to them)</option>
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Number of People</label>
                      <select
                        value={sessionForm.people}
                        onChange={e => handleSessionChange('people', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all bg-white"
                      >
                        <option value={1}>1 Person</option>
                        <option value={2}>2 People</option>
                        <option value={3}>3 People</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Session Value (Card Amount)</label>
                      <input
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100"
                        readOnly
                        value={sessionForm.baseAmount ? `GHS ${sessionForm.baseAmount}` : ''}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        You will pay approximately GHS {sessionForm.amount || ''}{' '}
                        {applyPaystackFeeUplift ? '(includes 2% charges fee)' : '(price as shown)'}.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                      <input className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100" readOnly value={sessionForm.duration ? `${sessionForm.duration} minutes` : ''} />
                    </div>
                  </div>
                  {/* Creator Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Creator Name <span className="text-red-500">*</span></label>
                      <input type="text" required value={sessionForm.creatorName} onChange={e => handleSessionChange('creatorName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all" placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Creator Phone <span className="text-red-500">*</span></label>
                      <input type="tel" required value={sessionForm.creatorPhone} onChange={e => handleSessionChange('creatorPhone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all" placeholder="024 XXX XXXX" />
                    </div>
                  </div>

                  {/* Recipient Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                      <Heart className="w-5 h-5 text-pink-500" />
                      <span>Recipient Details</span>
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Name <span className="text-red-500">*</span></label>
                      <input type="text" required value={sessionForm.recipientName} onChange={e => handleSessionChange('recipientName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all" placeholder="Enter recipient's full name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Phone Number</label>
                      <input type="tel" value={sessionForm.recipientPhone} onChange={e => handleSessionChange('recipientPhone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all" placeholder="+233 XX XXX XXXX" />
                    </div>
                    <p className="text-xs text-gray-500">
                      {sessionForm.deliveryMethod === 'digital' 
                        ? '* Phone number is required for digital delivery'
                        : '* Phone number is required for physical delivery'}
                    </p>
                  </div>
                  {/* Delivery Address - Only for Physical Delivery */}
                  {sessionForm.deliveryMethod === 'physical' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Delivery Address <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={sessionForm.address}
                        onChange={e => handleSessionChange('address', e.target.value)}
                        placeholder="Enter delivery address..."
                        rows={3}
                        required={sessionForm.deliveryMethod === 'physical'}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all resize-none"
                      />
                    </div>
                  )}
                  {/* Optional Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Personal Message (Optional)</label>
                    <textarea value={sessionForm.message} onChange={e => handleSessionChange('message', e.target.value)} placeholder="Add a personal message for the recipient..." rows={4} maxLength={200} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all resize-none" />
                    <p className="text-xs text-gray-500 mt-1">{sessionForm.message.length}/200 characters</p>
                  </div>
                  {/* Delivery Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Delivery Method</label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-400 transition-all">
                        <input type="radio" name="session-deliveryMethod" value="digital" checked={sessionForm.deliveryMethod === 'digital'} onChange={e => handleSessionChange('deliveryMethod', e.target.value)} className="w-4 h-4 text-green-500" />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">Digital Delivery</div>
                          <div className="text-sm text-gray-600">Sent instantly via SMS or WhatsApp</div>
                        </div>
                      </label>
                      <label className="flex items-center space-x-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-green-400 transition-all">
                        <input type="radio" name="session-deliveryMethod" value="physical" checked={sessionForm.deliveryMethod === 'physical'} onChange={e => handleSessionChange('deliveryMethod', e.target.value)} className="w-4 h-4 text-green-500" />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">Physical Card</div>
                          <div className="text-sm text-gray-600">Delivered within 24-48 hours (delivery fee applies)</div>
                        </div>
                      </label>
                    </div>
                  </div>
                  {/* Terms Notice */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      By proceeding, you agree to our{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModalType('terms')}
                        className="text-blue-900 underline font-semibold hover:text-blue-950"
                      >
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModalType('privacy')}
                        className="text-blue-900 underline font-semibold hover:text-blue-950"
                      >
                        Privacy Policy
                      </button>.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSessionForm(false)
                        setShowSessionCompleteButton(false)
                        setSessionPaymentReference(null)
                        if (!isSessionSubmitting) {
                          resetSessionForm()
                        }
                      }}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSessionSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSessionSubmitting}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                    >
                      {isSessionSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <Gift className="w-5 h-5" />
                          <span>Proceed to Payment</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          )}
    </div>
  )
}

export default GiftCard

